import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import type { BrandAsset } from '../../services/editingApi';
import type { EditList, Position, TextOverlay } from '../../utils/editing/editList';
import { RESOLUTION_PIXELS } from '../../utils/editing/editList';

/**
 * What the renderer will draw, drawn over the preview instead: pictures,
 * text and captions at their times, and the frame a reframe keeps. Positions
 * are shares of the frame, exactly as the compiler resolves them, so the
 * preview and the export agree.
 */

export interface PreviewCue {
  startMs: number;
  endMs: number;
  lines: string[];
}

interface PreviewOverlayProps {
  list: EditList;
  outputMs: number;
  assets: BrandAsset[];
  cues: PreviewCue[];
  /** The source's picture size, to work out the reframe window. */
  videoWidth: number;
  videoHeight: number;
}

const FONT_STACKS = {
  sans: '"Noto Sans", "Helvetica Neue", Arial, sans-serif',
  serif: '"Noto Serif", Georgia, serif',
  mono: '"Noto Sans Mono", Menlo, monospace',
};

const ASPECTS: Record<string, number> = { '16:9': 16 / 9, '9:16': 9 / 16, '1:1': 1, '4:5': 4 / 5 };

/** CSS for a position: anchored to an edge with a margin, or centred on a point. */
export function positionStyle(position: Position, frameHeightPx: number): React.CSSProperties {
  if (position.xPct !== null && position.yPct !== null) {
    return { left: `${position.xPct}%`, top: `${position.yPct}%`, transform: 'translate(-50%, -50%)' };
  }
  const margin = (position.marginPct / 100) * frameHeightPx;
  const style: React.CSSProperties = {};
  const a = position.anchor;
  if (a === 'top-left' || a === 'left' || a === 'bottom-left') style.left = margin;
  else if (a === 'top-right' || a === 'right' || a === 'bottom-right') style.right = margin;
  else { style.left = '50%'; style.transform = 'translateX(-50%)'; }
  if (a === 'top-left' || a === 'top' || a === 'top-right') style.top = margin;
  else if (a === 'bottom-left' || a === 'bottom' || a === 'bottom-right') style.bottom = margin;
  else {
    style.top = '50%';
    style.transform = style.transform ? 'translate(-50%, -50%)' : 'translateY(-50%)';
  }
  return style;
}

/** "#RRGGBBAA" → rgba(); "#RRGGBB" stays. */
export const cssColor = (hex: string | null): string | undefined => {
  if (!hex) return undefined;
  if (hex.length === 9) {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    const a = parseInt(hex.slice(7, 9), 16) / 255;
    return `rgba(${r}, ${g}, ${b}, ${Math.round(a * 1000) / 1000})`;
  }
  return hex;
};

const fadeOpacity = (text: TextOverlay, outputMs: number): number => {
  if (text.fadeMs <= 0) return 1;
  const sinceStart = outputMs - text.fromMs;
  const untilEnd = text.toMs - outputMs;
  return Math.max(0, Math.min(1, sinceStart / text.fadeMs, untilEnd / text.fadeMs));
};

/** The window a reframe keeps, as shares of the source frame (crop mode). */
export function reframeWindow(list: EditList, videoWidth: number, videoHeight: number): { left: number; top: number; width: number; height: number } | null {
  if (list.output.aspect === 'source' || !videoWidth || !videoHeight || (list.reframe && list.reframe.mode === 'pad')) return null;
  const target = ASPECTS[list.output.aspect];
  const source = videoWidth / videoHeight;
  if (Math.abs(source - target) / target < 0.005) return null;
  const focusX = (list.reframe?.focusXPct ?? 50) / 100;
  const focusY = (list.reframe?.focusYPct ?? 50) / 100;
  let width = 1;
  let height = 1;
  if (source > target) width = target / source; else height = source / target;
  const left = Math.min(Math.max(focusX - width / 2, 0), 1 - width);
  const top = Math.min(Math.max(focusY - height / 2, 0), 1 - height);
  return { left, top, width, height };
}

const PreviewOverlay: React.FC<PreviewOverlayProps> = ({ list, outputMs, assets, cues, videoWidth, videoHeight }) => {
  const [frameEl, setFrameEl] = React.useState<HTMLDivElement | null>(null);
  const frameHeight = frameEl?.clientHeight || 0;
  const frameWidth = frameEl?.clientWidth || 0;
  const assetById = useMemo(() => {
    const map: Record<string, BrandAsset> = {};
    assets.forEach((asset) => { map[asset.id] = asset; });
    return map;
  }, [assets]);
  const cue = cues.find((c) => outputMs >= c.startMs && outputMs < c.endMs) || null;
  const window = reframeWindow(list, videoWidth, videoHeight);
  const captionSize = list.captions ? (list.captions.style.sizePct / 100) * frameHeight : 0;
  const outputPixels = RESOLUTION_PIXELS[list.output.resolution];

  return (
    <Box data-testid="preview-overlay" sx={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
      {/* Everything placed on the result sits in the output frame: the reframe window when there is one (the export is
          that window), else the whole picture. Sizes and margins are shares of this frame, as in the render. */}
      <Box
        ref={setFrameEl}
        data-testid="output-frame"
        sx={window
          ? { position: 'absolute', left: `${window.left * 100}%`, top: `${window.top * 100}%`, width: `${window.width * 100}%`, height: `${window.height * 100}%` }
          : { position: 'absolute', inset: 0 }}
      >
      {list.overlays.map((overlay) => {
        const asset = assetById[overlay.assetId];
        const visible = (overlay.fromMs === null || outputMs >= overlay.fromMs) && (overlay.toMs === null || outputMs < overlay.toMs);
        if (!asset || !visible) return null;
        return (
          <img
            key={overlay.id}
            src={asset.url}
            alt=""
            data-testid={`preview-picture-${overlay.id}`}
            style={{ position: 'absolute', width: `${overlay.widthPct}%`, opacity: overlay.opacity, ...positionStyle(overlay.position, frameHeight) }}
          />
        );
      })}
      {list.texts.map((text) => {
        if (outputMs < text.fromMs || outputMs >= text.toMs) return null;
        const sizePx = (text.sizePct / 100) * frameHeight;
        const background = cssColor(text.backgroundColor);
        return (
          <Box
            key={text.id}
            data-testid={`preview-text-${text.id}`}
            sx={{
              position: 'absolute',
              maxWidth: '86%',
              opacity: fadeOpacity(text, outputMs),
              fontFamily: FONT_STACKS[text.font],
              fontSize: sizePx,
              fontWeight: text.style === 'plain' ? 400 : 700,
              lineHeight: 1.15,
              color: text.color,
              textAlign: text.position.anchor === 'center' || text.position.anchor === 'top' || text.position.anchor === 'bottom' ? 'center' : 'left',
              textShadow: background ? 'none' : '0 0 6px rgba(0,0,0,0.9), 0 1px 2px rgba(0,0,0,0.9)',
              bgcolor: background,
              px: background ? `${sizePx * 0.35}px` : 0,
              py: background ? `${sizePx * 0.15}px` : 0,
              borderRadius: background ? `${sizePx * 0.12}px` : 0,
              whiteSpace: 'pre-wrap',
              ...positionStyle(text.position, frameHeight),
            }}
          >
            {text.text}
            {text.secondaryText ? <Box component="span" sx={{ display: 'block', fontSize: sizePx * 0.66, fontWeight: 400 }}>{text.secondaryText}</Box> : null}
          </Box>
        );
      })}
      {cue && list.captions ? (
        <Box
          data-testid="preview-caption"
          sx={{
            position: 'absolute',
            left: '6%',
            right: '6%',
            [list.captions.style.placement === 'top' ? 'top' : 'bottom']: '5%',
            textAlign: 'center',
          }}
        >
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              fontFamily: FONT_STACKS[list.captions.style.font],
              fontSize: captionSize,
              lineHeight: 1.25,
              color: list.captions.style.color,
              bgcolor: cssColor(list.captions.style.backgroundColor),
              textShadow: list.captions.style.backgroundColor ? 'none' : '0 0 6px rgba(0,0,0,0.9)',
              px: `${captionSize * 0.3}px`,
              py: `${captionSize * 0.1}px`,
              borderRadius: `${captionSize * 0.1}px`,
              whiteSpace: 'pre-line',
            }}
          >
            {cue.lines.join('\n')}
          </Box>
        </Box>
      ) : null}
      </Box>
      {window ? (
        <>
          <Box data-testid="reframe-window" sx={{ position: 'absolute', left: `${window.left * 100}%`, top: `${window.top * 100}%`, width: `${window.width * 100}%`, height: `${window.height * 100}%`, boxShadow: '0 0 0 9999px rgba(0,0,0,0.55)', border: '2px dashed rgba(255,255,255,0.9)' }} />
          <Box sx={{ position: 'absolute', left: 8, bottom: 8, color: '#fff', fontSize: 12, bgcolor: 'rgba(0,0,0,0.6)', px: 1, py: 0.25, borderRadius: 1 }}>
            {list.output.aspect} · {outputPixels}p · what’s outside the frame is cut
          </Box>
        </>
      ) : null}
      {frameWidth === 0 ? null : null}
    </Box>
  );
};

export default PreviewOverlay;
